/**
 * AppICEMAP — ReportMarker
 * DivIcon factory for category-specific report markers on Leaflet.
 *
 * Every category renders the same round badge — a light ring around a filled
 * disc with the category glyph in the middle — and is told apart by its colour
 * and icon, not by its silhouette. The `marker_shape` field is therefore no
 * longer used for the outline.
 *
 * States:
 *   .marker--new       — pulse animation (<5 min old)
 *   .marker--expiring  — 50% opacity + desaturated
 *   .marker--low-karma — 40% opacity (is_limited && confirmations < 2)
 */

import L from "leaflet";
import { getCategoryStyle } from "../../utils/categoryStyle.js";
import { isExpired, getRemainingFraction } from "../../utils/expiry.js";

/**
 * Map of category color_ramp values to CSS custom property names.
 * Falls back to accent for unknown ramps.
 */
const RAMP_COLORS = {
	"accent-100": "var(--color-accent-100)",
	"accent-200": "var(--color-accent-200)",
	"accent-300": "var(--color-accent-300)",
	"accent-400": "var(--color-accent-400)",
	"accent-500": "var(--color-accent-500)",
	"accent-600": "var(--color-accent-600)",
	"accent-700": "var(--color-accent-700)",
	"accent-800": "var(--color-accent-800)",
	"accent-900": "var(--color-accent-900)",
	"accent-2-100": "var(--color-accent-2-100)",
	"accent-2-200": "var(--color-accent-2-200)",
	"accent-2-300": "var(--color-accent-2-300)",
	"accent-2-400": "var(--color-accent-2-400)",
	"accent-2-500": "var(--color-accent-2-500)",
	"accent-2-600": "var(--color-accent-2-600)",
	"accent-2-700": "var(--color-accent-2-700)",
	"accent-2-800": "var(--color-accent-2-800)",
	"accent-2-900": "var(--color-accent-2-900)",
	"neutral-100": "var(--color-neutral-100)",
	"neutral-200": "var(--color-neutral-200)",
	"neutral-300": "var(--color-neutral-300)",
	"neutral-400": "var(--color-neutral-400)",
	"neutral-500": "var(--color-neutral-500)",
	"neutral-600": "var(--color-neutral-600)",
	"neutral-700": "var(--color-neutral-700)",
	"neutral-800": "var(--color-neutral-800)",
	"neutral-900": "var(--color-neutral-900)",
};

/**
 * Create a Leaflet DivIcon for a report marker.
 *
 * @param {object} report - Report object from Directus
 * @param {object} [options]
 * @param {boolean} [options.pulseNew=false] - Apply pulse animation for new reports
 * @param {boolean} [options.lowKarma=false] - Apply low-karma opacity
 * @param {number} [options.expiryOpacity=1.0] - Opacity based on expiry state
 * @returns {L.DivIcon}
 */
export function createReportMarker(report, options = {}) {
	const { pulseNew = false, lowKarma = false, expiryOpacity = 1.0 } = options;

	const category = report.category || {};
	const label = category.label || "categoría desconocida";

	const { color, ink, iconSvg, phosphor } = getCategoryStyle(category);

	// Palette colours are already mid-tone and sit under a glyph picked to
	// contrast with them. Ramp fallbacks are pale lavenders, so those get mixed
	// toward the ground colour to keep the white glyph readable.
	const badgeBg = iconSvg
		? color
		: `color-mix(in srgb, ${RAMP_COLORS[category.color_ramp] || "var(--color-accent-600)"} 78%, #10111c)`;

	const glyph = iconSvg || `<i class="ph ph-${phosphor}" aria-hidden="true"></i>`;

	// Build class list
	const classes = ["marker"];
	if (pulseNew) classes.push("marker--new");
	if (lowKarma) classes.push("marker--low-karma");
	if (expiryOpacity < 0.6) classes.push("marker--expiring");

	const className = classes.join(" ");

	// Determine overall opacity
	let opacity = expiryOpacity;
	if (lowKarma) opacity = Math.min(opacity, 0.4);

	const html = `
    <div class="${className}" data-report-id="${report.id}"
         style="opacity:${opacity};--marker-bg:${badgeBg};--marker-ink:${ink};--marker-color:${color}">
      <span class="marker__badge" role="img" aria-label="Reporte de ${label}">
        ${glyph}
      </span>
      <span class="marker__pulse-ring"></span>
    </div>
  `;

	return L.divIcon({
		className: "marker-container",
		html,
		iconSize: [40, 40],
		iconAnchor: [20, 20],
		popupAnchor: [0, -22],
	});
}

/**
 * Create the cluster icon for markerClusterGroup.
 * @param {L.MarkerCluster} cluster
 * @returns {L.DivIcon}
 */
export function createClusterIcon(cluster) {
	const count = cluster.getChildCount();
	let size = 40;
	if (count >= 10) size = 46;
	if (count >= 50) size = 52;
	if (count >= 100) size = 56;

	const html = `
    <div class="cluster-badge" style="width:${size}px;height:${size}px;">
      <span class="cluster-count">${count}</span>
    </div>
  `;

	return L.divIcon({
		className: "cluster-container",
		html,
		iconSize: [size, size],
		iconAnchor: [size / 2, size / 2],
	});
}

/**
 * Check if a report is "new" (<5 minutes old).
 * @param {object} report
 * @returns {boolean}
 */
export function isNewReport(report) {
	if (!report.created_at) return false;
	const created = new Date(report.created_at).getTime();
	const age = Date.now() - created;
	return age < 5 * 60 * 1000; // 5 minutes
}

/**
 * Check if a report has low-karma author.
 * @param {object} report
 * @returns {boolean}
 */
export function isLowKarmaReport(report) {
	const user = report.user_created || {};
	return user.is_limited === true && (report.confirmation_count || 0) < 2;
}

/**
 * Get the expiry opacity for a report based on its status and TTL.
 * @param {object} report
 * @returns {number} opacity value (0.0–1.0)
 */
export function getExpiryOpacity(report) {
	if (report.status === "expired" || isExpired(report)) return 0;

	// Fade over the last quarter of its life
	const remaining = getRemainingFraction(report);
	if (remaining < 0.1) return 0.45;
	if (remaining < 0.25) return 0.7;

	return 1.0;
}

